<script setup lang="ts">
import { computed } from 'vue'

/**
 * A bulleted list, with the indent and the markers set here rather than by the browser.
 *
 * Plain lines come through `items`; a point holding markup or a component is a `ListItem`
 * in the default slot. The two are not mixed — `items` wins and the slot is not rendered.
 *
 * There is no `<ol>`: numbered stages are `Steps`.
 */
const props = withDefaults(
  defineProps<{
    items?: (string | number)[]
    /**
     * `none` drops the bullets and keeps the indent, for a list that reads as a set of
     * sentences rather than of points. Add `flush` to drop the indent as well.
     */
    marker?: 'disc' | 'none'
    /** Space between points, px. 0 is the browser's own single line spacing. */
    gap?: number
    flush?: boolean
  }>(),
  { items: undefined, marker: 'disc', gap: 0, flush: false }
)

const gapCss = computed(() => `${props.gap}px`)
</script>

<template>
  <ul
    class="shared-list"
    :class="[`shared-list--${marker}`, { 'shared-list--flush': flush }]"
  >
    <template v-if="items">
      <li v-for="item in items" :key="item" class="shared-list__item">
        {{ item }}
      </li>
    </template>
    <slot v-else />
  </ul>
</template>

<style lang="scss" scoped>
.shared-list {
  margin: 0;
  padding-left: 20px;
}

.shared-list--disc {
  list-style: disc;
}

.shared-list--none {
  list-style: none;
}

.shared-list--flush {
  padding-left: 0;
}

/* `:deep` because a `ListItem` in the slot is compiled in the consumer's scope, so its
   `<li>` never carries this component's attribute. */
.shared-list :deep(li + li) {
  margin-top: v-bind(gapCss);
}
</style>
